import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../../Components/UI/dialog";
import { Button } from "../../../Components/UI/button";
import { Textarea } from "../../../Components/UI/textarea";
import { Label } from "../../../Components/UI/label";
import { X, Check, Loader2, Recycle, RefreshCw } from "lucide-react";
import { cn } from "../../../Lib/Cn";
import { getIntgnConfig } from "../../Settings/SubSettings/IntgnConfigAPI";
import { toast } from "sonner";
import WhatsAppIntgnConfig from "../WhatsAppDirectConfig/WhatsAppIntgnConfig";
import { resetInstanceWD, sendMessageWD } from "./WDConfigAPI";
import { getUserConfig } from "../../../Store/Reducers";

const messageTemplates = [
    { id: 1, title: "Follow-up", content: "Hello, just checking in regarding our previous conversation. Let me know if you have updates." },
    { id: 2, title: "Meeting Reminder", content: "Hi, this is a reminder for our meeting scheduled for tomorrow. Please confirm your availability." },
    { id: 3, title: "Payment Due", content: "Hello, this is a gentle reminder that invoice #123 is due soon. Please arrange payment." }
];

const BulkWhatsAppDialog = ({ isOpen = false, onClose, threadData }) => {
    const [selectedTemplateId, setSelectedTemplateId] = useState(messageTemplates[0].id);
    const [message, setMessage] = useState(messageTemplates[0].content);
    const [recipients, setRecipients] = useState([]);
    const [showConfig, setShowConfig] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [configData, setConfigData] = useState(null);

    // Fetch User Data & Check Configuration
    useEffect(() => {
        const checkConfiguration = async () => {
            if (!isOpen) return;

            setIsLoading(true);

            try {
                if (threadData) {
                    const dataArray = Array.isArray(threadData) ? threadData : [threadData];
                    const validRecipients = dataArray
						.filter(item => item?.connectionData?.mobile || item?.mobile)
						.map(item => {
							const cc =
							item?.connectionData?.cc ??
							item?.cc ??
							"";

							const mobile =
							item?.connectionData?.mobile ??
							item?.mobile ??
							"";

							return {
								id: item?.connID ?? item?.threadID,
								displayName:
									item?.connectionData?.bizName ??
									item?.bizName ??
									"Unknown Contact",
								fullPhoneNumber: `${cc}${mobile}`
							};
						})
						.filter(r => r.fullPhoneNumber); 

                    const uniqueRecipients = Array.from(new Set(validRecipients.map(a => a.fullPhoneNumber)))
                        .map(num => validRecipients.find(a => a.fullPhoneNumber === num));


                    setRecipients(uniqueRecipients);
                }

				let userConfigData = null;

				const userCookie = getUserConfig();

				if (userCookie) {

					if (userCookie?.WDConfig) {
					const cfgArr = JSON.parse(userCookie.WDConfig); // array
					const cfg = cfgArr?.[0];

					userConfigData = {
						WDProvider: cfg?.P ?? null,
						WDStatus: Number(userCookie?.WDStatus ?? 0),
						WDToken: cfg?.C ? JSON.stringify(cfg.C) : null
					};
					}
				}
		
				if (userCookie.WDStatus === 2) {
					setConfigData({
						token: userConfigData.WDToken,
						provider: userConfigData.WDProvider
					});
					setShowConfig(false);
				} else {
					setShowConfig(true);
				}
            } catch (err) {
                toast.error("Failed to verify WhatsApp configuration");
            } finally {
                setIsLoading(false);
            }
        };

        checkConfiguration();
    }, [isOpen, threadData, showConfig]);

    const buildPayload = () => {
        if (!configData?.token) return null;

        try {
            const tokenParsed = JSON.parse(configData.token);

			let payloadBase = {
                wdNumbers: recipients.map(r => r.fullPhoneNumber),
                textMessage: message
            };

            switch (configData.provider) {
                case "KWSP":
                    return [{
                        ...payloadBase,
                        accessToken: tokenParsed.accessToken,
                        instanceID: tokenParsed.instanceID
                    }];

                case "META":
                    return [{
                        ...payloadBase,
                        APIKey: tokenParsed.APIKey,
                    }];

                default:
                    return [{
                        ...payloadBase,
                        ...tokenParsed
                    }];
            }
        } catch (e) {
            console.error("Payload Build Error:", e);
            return null;
        }
    };

    const handleTemplateClick = (template) => {
        setMessage(template.content);
        setSelectedTemplateId(template.id);
    };

    const handleMessageChange = (e) => {
        setMessage(e.target.value);
        if (selectedTemplateId !== null) setSelectedTemplateId(null);
    };

    const removeRecipient = (fullPhoneNumberToRemove) => {
        setRecipients(recipients.filter(r => r.fullPhoneNumber !== fullPhoneNumberToRemove));
    };

    // Handle Send Message
    const handleSend = async () => {
        const payload = buildPayload();

        if (!payload) {
            toast.error("Invalid configuration credentials.");
            return;
        }

        setIsLoading(true);
        
        try {
            await sendMessageWD(configData.provider, payload);
            toast.success("Messages Sent.");
            onClose();
        } catch (error) {
            console.error(error);
            toast.error("Failed to send messages. Please check your connection.");
        } finally {
            setIsLoading(false);
        }
    };

	const handleReset = async () => {
		try {
			const tokenParsed = JSON.parse(configData.token);
			const response = await resetInstanceWD("WD", "KWSP", [tokenParsed]);
			toast.success("WhatsApp Instance Reset");
			onClose();
		} catch (error) {
			console.error("Reset failed", error);
			toast.error("Failed to reset instance.");
		}
	}

    if (showConfig && isOpen) {
        return (
            <WhatsAppIntgnConfig 
                onClose={() => {
                    setShowConfig(false);
                }} 
				onForceClose={()=>
					onClose()
				}
            />
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-xl p-0 gap-0 overflow-hidden">
                <DialogHeader className="p-4 border-b">
                    <div className="flex items-center justify-between w-full">
                        <DialogTitle className="flex items-center gap-2">
                            Send WhatsApp ({recipients.length})
                            {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                        </DialogTitle>
						<div className="flex items-center">
							<Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleReset}>	
								<RefreshCw className="h-4 w-4" />
							</Button>
							
							<DialogClose asChild>
								<Button variant="ghost" size="icon" className="h-8 w-8">
									<X className="h-4 w-4" />
								</Button>
							</DialogClose>

						</div>
                    </div>
                </DialogHeader>

                <div className="flex flex-col p-4 gap-5 overflow-y-auto max-h-[70vh]">
                    <div className="w-full">
                        <Label className="mb-2 block text-sm font-medium text-text-color">Message Content</Label>
                        <Textarea
                            value={message}
                            onChange={handleMessageChange}
                            placeholder="Type your message here..."
                            className="min-h-[120px] resize-none w-full sahi-input"
                        />
                    </div>

                    <div className="w-full">
                        <Label className="mb-2 block text-sm font-medium text-text-color">Quick Templates</Label>
                        <div className="grid grid-cols-1 gap-2">
                            {messageTemplates.map((template) => (
                                <div
                                    key={template.id}
                                    onClick={() => handleTemplateClick(template)}
                                    className={cn(
                                        "cursor-pointer border rounded-md p-3 bg-bg-grey-light transition-colors group",
                                        selectedTemplateId === template.id
                                            ? "bg-bg-white border-[var(--sahi-primary-color)]"
                                            : "border-sahi-grey/70 hover:bg-bg-white"
                                    )}
                                >
                                    <h4 className="font-medium text-sm text-text-color">{template.title}</h4>
                                    <p className="text-xs text-sub-text-color line-clamp-1 mt-1">{template.content}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="w-full">
                        <Label className="mb-2 block text-sm font-medium text-text-color">Recipients</Label>
                        <div className="flex flex-wrap gap-2 min-h-7">
                            {recipients.length > 0 ? (
                                recipients.map((recipient, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center sahi-radius border px-2.5 py-0.5 text-xs font-semibold text-foreground bg-card hover:bg-card/80 h-7 sahi-badge"
                                    >
                                        {recipient.displayName}
                                        {recipients.length > 1 && (
                                            <X 
                                                className="ml-2 h-3 w-3 cursor-pointer" 
                                                onClick={() => removeRecipient(recipient.fullPhoneNumber)} 
                                            />
                                        )}
                                    </span>
                                ))
                            ) : (
                                <span className="text-sm text-sub-text-color italic">No valid numbers found.</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-4 border-t flex justify-start gap-3 bg-gray-50/50">
                    <Button
                        className="sahi-primary-button gap-2"
                        disabled={!message.trim() || recipients.length === 0 || isLoading || !configData}
                        onClick={handleSend}
                    >
                        {isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Check className="h-4 w-4" />
                        )}
                        {isLoading ? "Processing..." : "Send Message"}
                    </Button>
                    <DialogClose asChild>
                        <Button variant="outline" className="sahi-secondary-button">
                            Cancel
                        </Button>
                    </DialogClose>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default BulkWhatsAppDialog;